import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { User, Mail, Phone, Lock, ArrowRight } from 'lucide-react';
import { countryCodes, defaultCountryCode } from '../../utils/countryCodes';
import { extractErrorMessage } from '../../utils/errorMessage';
import { getPostLoginRedirect } from '../../utils/storage';
import settingsAPI from '../../api/settings';

// For quick access - top common countries with Brunei as default
const QUICK_COUNTRY_CODES = [
  { code: '+673', country: 'Brunei', flag: '🇧🇳' },
  { code: '+60', country: 'Malaysia', flag: '🇲🇾' },
  { code: '+65', country: 'Singapore', flag: '🇸🇬' },
  { code: '+62', country: 'Indonesia', flag: '🇮🇩' },
  { code: '+63', country: 'Philippines', flag: '🇵🇭' },
  { code: '+66', country: 'Thailand', flag: '🇹🇭' },
  { code: '+84', country: 'Vietnam', flag: '🇻🇳' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' },
  { code: '+82', country: 'South Korea', flag: '🇰🇷' },
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+64', country: 'New Zealand', flag: '🇳🇿' },
  { code: '+44', country: 'United Kingdom', flag: '🇬🇧' },
  { code: '+1', country: 'United States', flag: '🇺🇸' },
  { code: '+971', country: 'UAE', flag: '🇦🇪' },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
];

export default function AuthPage() {
  const navigate = useNavigate();
  const { login, signup, guestLogin, requestOTP, setGoogleUser } = useAuth();
  const [loading, setLoading] = useState(false);
  // Tri-state: null = not yet known (settings still loading — render as
  // hidden, same as `false`), true/false = the real server value. Seeded
  // from the last known value cached in localStorage (a lightweight
  // stand-in for server-side rendering in this SPA) so repeat visits show
  // the correct tab immediately instead of flashing "Guest" and then
  // hiding it once the real /settings/public response arrives.
  const [guestLoginEnabled, setGuestLoginEnabled] = useState(() => {
    try {
      const cached = window.localStorage.getItem('cachedGuestLoginEnabled');
      return cached === null ? null : cached === 'true';
    } catch {
      return null;
    }
  });
  const [activeTab, setActiveTab] = useState('login');

  // Fetch public settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      const settings = await settingsAPI.getPublicSettings();
      const enabled = settings.guest_login_enabled ?? true;
      setGuestLoginEnabled(enabled);
      try {
        window.localStorage.setItem('cachedGuestLoginEnabled', String(enabled));
      } catch {
        // localStorage unavailable (e.g. private mode) — safe to ignore,
        // just means the next visit won't have a cached value either.
      }

      // If guest login is disabled and user was on guest tab, switch to login
      if (!enabled && activeTab === 'guest') {
        setActiveTab('login');
      }
    };
    fetchSettings();
  }, []);

  // Login state
  // Hydrate email + checkbox preference from localStorage on first render so
  // the input is already filled when the page mounts (no flicker).
  //   - `lastEmail`         → last successful login email (written by handleLogin)
  //   - `rememberEmailPref` → 'true' | 'false' — sticky user preference
  // First-ever visitor: pref is null → default to true (matches the user's spec).
  const initialRememberPref =
    typeof window !== 'undefined' && window.localStorage.getItem('rememberEmailPref') === 'false'
      ? false
      : true;
  const initialLastEmail =
    typeof window !== 'undefined' && initialRememberPref
      ? window.localStorage.getItem('lastEmail') || ''
      : '';
  const [loginEmail, setLoginEmail] = useState(initialLastEmail);
  const [loginPassword, setLoginPassword] = useState('');
  const [rememberEmail, setRememberEmail] = useState(initialRememberPref);

  /** Toggle handler — applies the "Uncheck = remove immediately" rule from spec. */
  const handleRememberToggle = (checked) => {
    setRememberEmail(checked);
    window.localStorage.setItem('rememberEmailPref', checked ? 'true' : 'false');
    if (!checked) {
      window.localStorage.removeItem('lastEmail');
      setLoginEmail('');
    }
  };

  /** "Not your device?" — clears the saved email AND un-ticks the box so
   *  the next successful login won't re-save automatically. */
  const handleClearSavedEmail = () => {
    window.localStorage.removeItem('lastEmail');
    window.localStorage.setItem('rememberEmailPref', 'false');
    setRememberEmail(false);
    setLoginEmail('');
    toast.success('Saved email cleared');
  };

  // Signup state
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupCountryCode, setSignupCountryCode] = useState('+673'); // Default to Brunei
  const [signupPhone, setSignupPhone] = useState('');
  const [signupPassword, setSignupPassword] = useState('');

  // Guest state
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestCountryCode, setGuestCountryCode] = useState('+673'); // Default to Brunei
  const [guestPhone, setGuestPhone] = useState('');
  const [guestOTP, setGuestOTP] = useState('');
  const [otpRequested, setOtpRequested] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginEmail, loginPassword);
      // Save email only on a confirmed-good login (option 1-a from spec).
      // Mirror to localStorage when the box is ticked; otherwise actively
      // clear any leftover value so we never persist behind the user's back.
      if (rememberEmail) {
        window.localStorage.setItem('lastEmail', loginEmail);
      } else {
        window.localStorage.removeItem('lastEmail');
      }
      toast.success('Welcome back!');
      navigate(getPostLoginRedirect());
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Concatenate country code with local phone number
      const fullPhone = signupCountryCode + signupPhone;
      const result = await signup(signupName, signupEmail, fullPhone, signupPassword);
      
      // New flow: Check if email verification is required
      if (result.requires_verification) {
        toast.success('Verification email sent! Please check your inbox.');
        // Redirect to email verification page
        navigate('/verify-email', { 
          state: { 
            email: signupEmail,
            name: signupName
          } 
        });
      } else {
        // Legacy flow (should not happen with new backend)
        toast.success('Account created successfully!');
        navigate(getPostLoginRedirect());
      }
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Signup failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOTP = async () => {
    if (!guestName.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!guestEmail.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    if (!guestPhone) {
      toast.error('Please enter phone number');
      return;
    }
    setLoading(true);
    try {
      await requestOTP(guestCountryCode, guestPhone, guestEmail, guestName);
      setOtpRequested(true);
      toast.success('Login code sent! Check your email inbox.');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to send login code'));
    } finally {
      setLoading(false);
    }
  };

  const handleGuestLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await guestLogin(guestName, guestCountryCode, guestPhone, guestEmail, guestOTP);
      toast.success('Welcome!');
      // Small delay to ensure state updates
      setTimeout(() => navigate(getPostLoginRedirect()), 100);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Invalid login code'));
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
  // THIS BREAKS THE AUTH — always derive the redirect from window.location.origin.
  //
  // Uses standard OAuth 2.0 Authorization Code redirect — no external script needed,
  // fully CSP-safe. Google redirects back to /auth/google/callback?code=xxx which
  // AuthCallback.jsx exchanges for a JWT via the backend.
  const handleGoogleSignIn = () => {
    const redirectUri = `${window.location.origin}/auth/google/callback`;
    // SECURITY (CSRF): generate a one-time state token and stash it in this
    // tab's sessionStorage. AuthCallback.jsx checks Google's returned `state`
    // against this before ever calling the backend — closes the OAuth login
    // CSRF hole (an attacker feeding a victim a crafted callback URL).
    const state = crypto.randomUUID();
    sessionStorage.setItem('google_oauth_state', state);
    const params = new URLSearchParams({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid email profile',
      access_type: 'offline',
      prompt: 'select_account',
      state,
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  };

  const GoogleSignInBlock = () => (
    <div className="mb-5">
      <button
        type="button"
        data-testid="google-signin-button"
        onClick={handleGoogleSignIn}
        className="w-full h-14 rounded-2xl border border-border bg-background flex items-center justify-center gap-3 font-semibold text-sm transition-all hover:bg-secondary active:scale-[0.98]"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
        Continue with Google
      </button>
      <div className="relative flex items-center gap-3 mt-5">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider">or continue with email</span>
        <div className="flex-1 h-px bg-border" />
      </div>
    </div>
  );

  return (
    <div
      className="mobile-container min-h-screen flex flex-col"
      style={{ background: '#F5F7FA' }}
    >
      {/* Navy top bar */}
      <div style={{ background: '#1A1A2E', height: 4 }} aria-hidden="true" />

      {/* Hero Section — BBA court background + navy overlay */}
      <div
        className="relative w-full"
        style={{
          height: 220,
          backgroundImage: "url('/images/courtview2_clean.jpeg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center 60%',
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(rgba(0,20,60,0.72), rgba(0,20,60,0.72))',
          }}
        />
        <div className="relative h-full flex flex-col items-center justify-center text-white px-6 text-center">
          <img
            src="/images/bba-logo.jpg"
            alt="BBA"
            style={{
              width: 90,
              height: 90,
              borderRadius: '50%',
              objectFit: 'cover',
              filter: 'drop-shadow(0 4px 16px rgba(0,0,0,0.45))',
              border: '3px solid #FFFFFF',
              background: '#FFFFFF',
            }}
          />
          <h1
            style={{
              fontFamily: 'Oswald, sans-serif',
              fontSize: '22px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginTop: 12,
              marginBottom: 4,
              lineHeight: 1.15,
            }}
          >
            Brunei Basketball Association
          </h1>
          <p style={{ fontSize: '14px', opacity: 0.85, margin: 0 }}>
            Book Your Game, Own The Court
          </p>
        </div>
      </div>

      {/* Auth Tabs */}
      <div className="flex-1 p-6" style={{ maxWidth: 420, width: '100%', margin: '0 auto' }}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={`grid w-full ${guestLoginEnabled ? 'grid-cols-3' : 'grid-cols-2'} mb-6 bba-auth-tabs`}>
            <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
            <TabsTrigger value="signup" data-testid="signup-tab">Sign Up</TabsTrigger>
            {guestLoginEnabled && (
              <TabsTrigger value="guest" data-testid="guest-tab">Guest</TabsTrigger>
            )}
          </TabsList>

          {/* Google Sign-In sits above the email form for Login and Sign Up only */}
          {activeTab !== 'guest' && <GoogleSignInBlock />}

          {/* Login Tab */}
          <TabsContent value="login">
            <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form">
              <div>
                <Label htmlFor="login-email" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="login-email"
                    data-testid="login-email-input"
                    type="email"
                    placeholder="your@email.com"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    className="h-14 rounded-2xl bg-secondary border-transparent focus:border-primary pl-12"
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="login-password" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="login-password"
                    data-testid="login-password-input"
                    type="password"
                    placeholder="••••••••"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="h-14 rounded-2xl bg-secondary border-transparent focus:border-primary pl-12"
                    required
                  />
                </div>
              </div>
              <div className="flex items-center justify-between -mt-1">
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="remember-email-row">
                  <input
                    type="checkbox"
                    checked={rememberEmail}
                    onChange={(e) => handleRememberToggle(e.target.checked)}
                    className="h-4 w-4 rounded border-muted-foreground/40 accent-[#FF6B00]"
                    data-testid="remember-email-checkbox"
                  />
                  <span>Remember my email on this device</span>
                </label>
                {loginEmail && (
                  <button
                    type="button"
                    onClick={handleClearSavedEmail}
                    className="text-xs underline text-muted-foreground hover:text-foreground"
                    data-testid="clear-saved-email-link"
                  >
                    Not your device? Clear
                  </button>
                )}
              </div>
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm font-semibold"
                  style={{ color: '#FF6B00' }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#E85F00'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = '#FF6B00'; }}
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </button>
              </div>
              <Button 
                type="submit" 
                data-testid="login-submit-button"
                className="w-full btn-primary h-14 mt-6"
                disabled={loading}
              >
                {loading ? 'Logging in...' : 'Login'}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </form>
          </TabsContent>

          {/* Signup Tab */}
          <TabsContent value="signup">
            <form onSubmit={handleSignup} className="space-y-5" data-testid="signup-form">
              <div>
                <Label htmlFor="signup-name" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
                  Name
                </Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="signup-name"
                    data-testid="signup-name-input"
                    type="text"
                    placeholder="John Doe"
                    value={signupName}
                    onChange={(e) => setSignupName(e.target.value)}
                    className="h-14 rounded-2xl bg-secondary border-transparent focus:border-primary pl-12"
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="signup-email" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="signup-email"
                    data-testid="signup-email-input"
                    type="email"
                    placeholder="your@email.com"
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    className="h-14 rounded-2xl bg-secondary border-transparent focus:border-primary pl-12"
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="signup-phone" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
                  Phone Number
                </Label>
                <div className="flex gap-2">
                  <Select 
                    value={signupCountryCode} 
                    onValueChange={setSignupCountryCode}
                  >
                    <SelectTrigger 
                      data-testid="signup-country-code-select"
                      className="h-14 rounded-2xl bg-secondary border-transparent focus:border-primary w-[140px]"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {/* Dedupe by dial code — Radix Select uses `value` as the
                          React key of an internal hidden <option>, so entries
                          sharing a dial code (e.g. +1 US/Canada, +7 RU/KZ)
                          would otherwise trigger duplicate-key warnings. */}
                      {Array.from(new Map(countryCodes.map(c => [c.code, c])).values()).map((country) => (
                        <SelectItem key={country.code + country.country} value={country.code}>
                          <span className="flex items-center gap-2">
                            <span>{country.flag}</span>
                            <span>{country.code}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="relative flex-1">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="signup-phone"
                      data-testid="signup-phone-input"
                      type="tel"
                      placeholder="Enter phone number"
                      value={signupPhone}
                      onChange={(e) => setSignupPhone(e.target.value.replace(/\D/g, ''))}
                      className="h-14 rounded-2xl bg-secondary border-transparent focus:border-primary pl-12"
                      required
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">
                  Enter phone number without country code
                </p>
              </div>
              <div>
                <Label htmlFor="signup-password" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="signup-password"
                    data-testid="signup-password-input"
                    type="password"
                    placeholder="••••••••"
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                    className="h-14 rounded-2xl bg-secondary border-transparent focus:border-primary pl-12"
                    required
                  />
                </div>
              </div>
              <Button 
                type="submit" 
                data-testid="signup-submit-button"
                className="w-full btn-primary h-14 mt-6"
                disabled={loading}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <p className="text-center text-xs text-muted-foreground mt-3" data-testid="signup-terms-notice">
                By registering, you agree to our{' '}
                <Link
                  to="/terms"
                  className="underline hover:opacity-80 transition-opacity"
                  style={{ color: '#FF6B00' }}
                >
                  Terms &amp; Conditions
                </Link>
              </p>
            </form>
          </TabsContent>

          {/* Guest Tab */}
          {guestLoginEnabled && (
            <TabsContent value="guest">
            <form onSubmit={handleGuestLogin} className="space-y-5" data-testid="guest-form">
              <div>
                <Label htmlFor="guest-name" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
                  Your Name
                </Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="guest-name"
                    data-testid="guest-name-input"
                    type="text"
                    placeholder="Enter your name"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    className="h-14 rounded-2xl bg-secondary border-transparent focus:border-primary pl-12"
                    required
                    disabled={otpRequested}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="guest-email" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="guest-email"
                    data-testid="guest-email-input"
                    type="email"
                    placeholder="your@email.com"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    className="h-14 rounded-2xl bg-secondary border-transparent focus:border-primary pl-12"
                    required
                    disabled={otpRequested}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">
                  Your login code will be sent here
                </p>
              </div>
              <div>
                <Label htmlFor="guest-phone" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
                  Phone Number
                </Label>
                <div className="flex gap-2">
                  <Select 
                    value={guestCountryCode} 
                    onValueChange={setGuestCountryCode}
                    disabled={otpRequested}
                  >
                    <SelectTrigger 
                      data-testid="guest-country-code-select"
                      className="h-14 rounded-2xl bg-secondary border-transparent focus:border-primary w-[140px]"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {QUICK_COUNTRY_CODES.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          <span className="flex items-center gap-2">
                            <span>{country.flag}</span>
                            <span>{country.code}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="relative flex-1">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="guest-phone"
                      data-testid="guest-phone-input"
                      type="tel"
                      placeholder="Enter phone number"
                      value={guestPhone}
                      onChange={(e) => setGuestPhone(e.target.value.replace(/\D/g, ''))}
                      className="h-14 rounded-2xl bg-secondary border-transparent focus:border-primary pl-12"
                      required
                      disabled={otpRequested}
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">
                  Enter phone number without country code
                </p>
              </div>
              {!otpRequested ? (
                <Button 
                  type="button"
                  data-testid="request-otp-button"
                  onClick={handleRequestOTP}
                  className="w-full btn-primary h-14"
                  disabled={loading}
                >
                  {loading ? 'Sending OTP...' : 'Request OTP'}
                </Button>
              ) : (
                <>
                  <div>
                    <Label htmlFor="guest-otp" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
                      Login Code
                    </Label>
                    <Input
                      id="guest-otp"
                      data-testid="guest-otp-input"
                      type="text"
                      inputMode="numeric"
                      placeholder="Enter 6-digit code"
                      value={guestOTP}
                      onChange={(e) => setGuestOTP(e.target.value.replace(/\D/g, ''))}
                      className="h-14 rounded-2xl bg-secondary border-transparent focus:border-primary tracking-widest text-center text-xl font-bold"
                      maxLength={6}
                      required
                    />
                    <p className="text-xs text-muted-foreground mt-1.5">
                      Check <strong>{guestEmail}</strong> for your code
                    </p>
                  </div>
                  <Button 
                    type="submit" 
                    data-testid="guest-submit-button"
                    className="w-full btn-primary h-14"
                    disabled={loading}
                  >
                    {loading ? 'Verifying...' : 'Verify & Continue'}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    data-testid="change-phone-button"
                    onClick={() => {
                      setOtpRequested(false);
                      setGuestOTP('');
                    }}
                    className="w-full"
                  >
                    Change Details
                  </Button>
                </>
              )}
            </form>
          </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}